import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';
import { Form, Control, actions as formActions } from 'react-redux-form';
import FontAwesome from 'react-fontawesome';
import Select from 'react-select';
import RuleControl from './RuleControl.jsx';
import ControlGroup from '../forms/ControlGroup';
import DateSelector from '../forms/DateSelector';
import TimePicker from '../forms/TimePicker';
import SelectContacts from '../forms/SelectContacts';
import ValidationErrors from '../forms/ValidationErrors';
import { customers as customersDuck, admins as admin } from 'ducks';
import { entity as checklistItem } from '../../ducks/checklistItems';
import { actions, ruleFieldsQuery, ruleFieldsKey } from '../../ducks/communicationPlanEditor';
import { entity as status } from '../../ducks/statuses';
import { entity as field } from '../../ducks/fields';
import { entity as emailSnippet } from '../../ducks/emailSnippets';
import Placeholder from '../Placeholder';
import { sortBy } from '../../utils/arrays';
import moment from 'moment';
import { zonedTimeToUtc, format } from 'date-fns-tz';
import enUS from 'date-fns/locale/en-US';
import './styles/communicationPlanEditor.scss';
import './styles/ruleControl.scss';
import { singleDuck } from 'utils/duckify';

class AdminSelect extends React.Component {
  render() {
    const { model, admins, ...controlProps } = this.props;
    const options = admins.map(admin => {
      return { value: admin.id, label: `${admin.first_name} ${admin.last_name}` }
    });

    // Note: we use mapProps to extract the value from the option objects passed to onChange
    return (
      <Control.select
        model={model}
        multi={true}
        component={Select}
        options={options}
        disabled={admins.pending}
        mapProps={{ onChange: (props) => (selected) => props.onChange(selected.map(option => option.value)) }}
        {...controlProps}
      />
    );
  }
}

class CommunicationPlanEditor extends React.Component {

  //noinspection JSUnusedGlobalSymbols
  static childContextTypes = {
    errors: PropTypes.object
  };

  static propTypes = {
    workflow: PropTypes.string,
    // mapStateToProps properties
    admins: PropTypes.array,
    checklistItems: PropTypes.array,
    statuses: PropTypes.array,
    customer: PropTypes.object,
    emailSnippets: PropTypes.array,
    model: PropTypes.object,
    saving: PropTypes.object,
    errors: PropTypes.object
  };

  static mapStateToProps = (state) => {
    const customerDuck = singleDuck(customersDuck, 'current');
    return {
      admins: admin.all(state),
      checklistItems: checklistItem.all(state).sort(sortBy('disambiguated_name')),
      fields: field.all(state, ruleFieldsKey),
      customer: customerDuck.single(state, 'current'),
      statuses: status.all(state),
      emailSnippets: emailSnippet.allForWorkflow(state),
      ...state.communicationPlanEditor
    };
  };

  // Friendly field names for error message
  static errorFields = {
    type: 'Perform action',
    checklist_item_id: 'Checklist item',
    contact_status: 'Contact status',
    criteria: 'Additional criteria',
    recipient_types: 'Send to',
    snippet_id: 'Email template',
    snippet: 'Email Template',
    reminder_body: 'Reminder text',
    recipient_user_ids: 'Users',
    schedule_num: 'Number of days',
    schedule_date: 'Date'
  };

  //noinspection JSUnusedGlobalSymbols
  getChildContext() {
    return {
      errors: this.props.errors || {}
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;

    if (!this.props.admins.length) {
      dispatch(admin.actions.load());
    }

    if (!this.props.checklistItems.length) {
      dispatch(checklistItem.actions.load());
    }

    if (!this.props.statuses.length) {
      dispatch(status.actions.load());
    }

    if (!this.props.fields.length) {
      dispatch(field.actions.load(ruleFieldsQuery, ruleFieldsKey));
    }

    if (!this.props.emailSnippets.length) {
      dispatch(emailSnippet.actions.load());
    }

    // Bootstrap sets overflow to 'hidden' on body when a modal is visible
    // ...we want to undo that so that a modal that exceeds the height of the page can be scrolled
    document.body.style.overflowY = 'visible';
  }

  render() {
    const { onClose, admins, checklistItems, errors, fields, statuses, customer, emailSnippets, workflow, model, saving, dispatch } = this.props;
    const filteredChecklistItems = checklistItems.filter(item => item.workflow === workflow);
    const selectedChecklistItem = filteredChecklistItems.find(item => item.id === model.checklist_item_id) || {};
    const filteredStatuses = statuses.filter(status => status.workflow === workflow);
    const blankSelect = this.constructor.blankSelect;

    return (
      <Modal className="communication-plan-editor" show style={{ top: "50px" }} animation={false} backdrop='static' keyboard={false}>
        <Modal.Header>
          <h1 className='modal-title' style={{ display: 'inline-block' }}>Edit Plan Step</h1>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </Modal.Header>
        <Modal.Body>
          <Form model="communicationPlanEditor.model">
            <ValidationErrors errors={errors} fieldNames={this.constructor.errorFields} />

            <div className="form-horizontal">
              <ControlGroup label="Name">
                <Control.text placeholder={'Enter name for this plan step'} model={'.name'}></Control.text>
                <span className="help-block">
                  Optionally name this communication plan step. <br />
                  This is internal-facing only.
                </span>
              </ControlGroup>
              <ControlGroup label="Trigger">
                <Placeholder.select ready={checklistItems.ready}>
                  <Control.select model=".checklist_item_id"
                    className={blankSelect(model.checklist_item_id)}
                    onChange={() => dispatch(formActions.change('communicationPlanEditor.model.contact_status', ''))}>
                    <option className="select-item" value="">Select checklist item...</option>
                    {filteredChecklistItems.map(item => <option key={item.id} value={item.id}>{item.disambiguated_name}</option>)}
                  </Control.select>
                </Placeholder.select>

                {/* The 'name' property on the placeholder is needed for styling the placeholder */}
                <Placeholder.select ready={statuses.ready} name="communicationPlanEditor.model.contact_status">
                  <Control.select model=".contact_status"
                    className={blankSelect(model.contact_status)}
                    onChange={() => dispatch(formActions.change('communicationPlanEditor.model.checklist_item_id', ''))}>
                    <option value="">Select status...</option>
                    {filteredStatuses.map(status => <option key={status.id}
                      value={status.id}>{this.constructor.formatStatus(status.name)}</option>)}
                  </Control.select>
                </Placeholder.select>

                <span className="help-block">
                  {!(model.checklist_item_id || model.contact_status) && (
                    <span>Select a checklist item or status to trigger this step</span>)}
                  {model.checklist_item_id && (
                    <span>This step will trigger when the checklist item <span
                      className="comm-plan-trigger-value">{selectedChecklistItem.disambiguated_name}</span> is completed</span>)}
                  {model.contact_status && (
                    <span>This step will trigger when the contact enters the <span
                      className="comm-plan-trigger-value">{this.constructor.formatStatus(model.contact_status)}</span> status</span>)}
                </span>
              </ControlGroup>

              <ControlGroup label="Additional criteria">
                {fields.ready ?
                  <Control model=".criteria" availableFields={fields} component={RuleControl} /> :
                  <FontAwesome name="spinner" spin />}
              </ControlGroup>

              <ControlGroup label="Perform action" labelFor="planType">
                <Control.select id="planType" model=".type" className={blankSelect(model.type)}>
                  <option value="">Select...</option>
                  <option value="EmailCommunicationPlan">Send Email</option>
                  <option value="ReminderCommunicationPlan">Create Reminder</option>
                </Control.select>
              </ControlGroup>

              {/* Schedule options */}

              <ControlGroup label="At this time" labelFor="scheduleType">
                <Control.select id="scheduleType" model=".schedule_type" className={blankSelect(model.schedule_type)}>
                  <option value="">Select...</option>
                  <option value="immediate">Immediately</option>
                  <option value="num_days">N days after trigger</option>
                  <option value="date">On date</option>
                </Control.select>
              </ControlGroup>

              {model.schedule_type === "num_days" && (
                <ControlGroup label="Number of Days">
                  <Control.text model=".schedule_num" placeholder="Number" className="input-small" />
                </ControlGroup>
              )}

              {model.schedule_type === "date" && (
                <ControlGroup label="Date">
                  <DateSelector model=".schedule_date" modelValue={model.schedule_date} className="input-small" />
                </ControlGroup>
              )}

              {(model.schedule_type === "date" || model.schedule_type === "num_days") && (
                <ControlGroup label="Time">
                  <TimePicker model=".schedule_time" defaultValue={model.schedule_time || '08:10'} modelValue={model.schedule_time} />
                  <span className={'timezone'}> {format(zonedTimeToUtc(model.schedule_date || moment().format("YYYY-MM-DD"), customer.value.time_zone), 'zzz', { timeZone: customer.value.time_zone, locale: enUS })}</span>
                </ControlGroup>
              )}


              {/* Email options */}

              {model.type === "EmailCommunicationPlan" && (
                <React.Fragment>
                  <ControlGroup label="Send to">
                    <SelectContacts model=".recipient_types[]" />
                    <label className="checkbox">
                      <Control.checkbox className='form-check-inline' model=".recipient_types[]" value="users" />Finalsite Enrollment user
                    </label>
                    <label className='form-label d-block'>
                      <AdminSelect
                        model=".recipient_user_ids"
                        admins={admins}
                        disabled={!model.recipient_types || model.recipient_types.indexOf('users') === -1} />
                    </label>
                  </ControlGroup>

                  <ControlGroup className="w-100" label="Email template" labelFor="snippetId">
                    <Placeholder.select ready={emailSnippets.length !== 0}>
                      <Control.select id="snippetId" model=".snippet_id" className={blankSelect(model.snippet_id)}>
                        <option value="">Select...</option>
                        {emailSnippets.map(snippet => {
                          const key = snippet.id;
                          return (<option key={key} value={key}>{snippet.name}</option>);
                        })}
                      </Control.select>
                    </Placeholder.select>
                  </ControlGroup>
                </React.Fragment>
              )}

              {/* Reminder options */}

              {model.type === "ReminderCommunicationPlan" && (
                <React.Fragment>
                  <ControlGroup label="Assign reminder to">
                    <div id="recipientUserIds">
                      <AdminSelect model=".recipient_user_ids" admins={admins.filter(myadmin => myadmin.acl > 0)} />
                    </div>
                  </ControlGroup>

                  <ControlGroup label="Reminder text" labelFor="reminderBody">
                    <Control.textarea id="reminderBody" model=".reminder_body" />
                  </ControlGroup>
                </React.Fragment>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} variant="secondary">Close</Button>
          <Button className="ms-1" onClick={() => dispatch(actions.save(model)).then(onClose)} variant="primary" disabled={saving[model.id]}>{saving[model.id] ? 'Saving...' : 'Save'}</Button>
        </Modal.Footer>
      </Modal>);
  }

  static blankSelect(value) {
    return value ? '' : 'comm-plan-blank-select';
  }

  static formatStatus(status) {
    return status.replace(/_/g, ' ');
  }
}

export default connect(CommunicationPlanEditor.mapStateToProps)(CommunicationPlanEditor);
